import { GALLERY_TYPES, ROLES } from '@/data/categories';
import type { GalleryItem, GalleryType, Question, Reply, Role } from '@/types';

/* -------------------------------------------------------------------------- */
/* Field-level validation                                                      */
/* -------------------------------------------------------------------------- */

/**
 * Accept only absolute http(s) URLs.
 *
 * Parsing with `URL` rather than a regular expression rejects the cases a
 * pattern quietly lets through — `javascript:` payloads, missing authority,
 * and protocol-relative URLs — which matters because these values end up in an
 * `href` that visitors click.
 */
export function isValidUrl(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) return false;

  let parsed: URL;
  try {
    parsed = new URL(trimmed);
  } catch {
    return false;
  }

  return (parsed.protocol === 'http:' || parsed.protocol === 'https:') && parsed.host.length > 0;
}

/**
 * Pragmatic e-mail check: one `@`, a dot-bearing domain, no whitespace.
 * Deliberately not RFC-5322 exhaustive — the goal is catching typos, and the
 * browser's own `type="email"` provides the second layer.
 */
export function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value.trim());
}

export function isNonEmpty(value: string): boolean {
  return value.trim().length > 0;
}

/* -------------------------------------------------------------------------- */
/* Persisted-shape validation                                                  */
/* -------------------------------------------------------------------------- */

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function str(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function isRole(value: unknown): value is Role {
  return typeof value === 'string' && (ROLES as readonly string[]).includes(value);
}

function isGalleryType(value: unknown): value is GalleryType {
  return typeof value === 'string' && (GALLERY_TYPES as readonly string[]).includes(value);
}

/**
 * Rebuild a gallery item from unknown persisted data.
 * Returns `null` when required fields are missing or the URL is unsafe.
 */
export function parseGalleryItem(value: unknown): GalleryItem | null {
  if (!isRecord(value)) return null;

  const id = str(value.id);
  const title = str(value.title);
  const url = str(value.url);

  if (!id || !title || !isGalleryType(value.type) || !isValidUrl(url)) return null;

  const thumbnail = str(value.thumbnail);

  return {
    id,
    type: value.type,
    title,
    description: str(value.description),
    url,
    // Drop a thumbnail that is present but unusable rather than rendering a broken image.
    thumbnail: thumbnail && isValidUrl(thumbnail) ? thumbnail : '',
    ...(value.seed === true ? { seed: true as const } : {}),
  };
}

function parseReply(value: unknown): Reply | null {
  if (!isRecord(value)) return null;

  const id = str(value.id);
  const author = str(value.author);
  const body = str(value.body);

  if (!id || !author || !body || !isRole(value.role)) return null;

  const label = str(value.label);

  return {
    id,
    author,
    role: value.role,
    createdAt: typeof value.createdAt === 'string' ? value.createdAt : null,
    ...(label ? { label } : {}),
    body,
  };
}

export function parseQuestion(value: unknown): Question | null {
  if (!isRecord(value)) return null;

  const id = str(value.id);
  const author = str(value.author);
  const body = str(value.body);

  if (!id || !author || !body || !isRole(value.role)) return null;

  const rawReplies = Array.isArray(value.replies) ? value.replies : [];
  const label = str(value.label);

  return {
    id,
    author,
    role: value.role,
    createdAt: typeof value.createdAt === 'string' ? value.createdAt : null,
    ...(label ? { label } : {}),
    body,
    replies: rawReplies.map(parseReply).filter((reply): reply is Reply => reply !== null),
  };
}

/**
 * Validate a persisted array, discarding entries that no longer parse.
 *
 * Returns `null` — meaning "fall back to the seed content" — when the payload
 * is not an array at all, or when it held entries and none of them survived
 * validation. A stored array that was already empty is a deliberate state the
 * visitor reached, so it is returned as-is rather than resurrecting the seeds.
 */
export function parseList<T>(value: unknown, parseItem: (item: unknown) => T | null): T[] | null {
  if (!Array.isArray(value)) return null;

  const parsed = value.map(parseItem).filter((item): item is T => item !== null);

  if (value.length > 0 && parsed.length === 0) return null;

  return parsed;
}
